// 🔴 N°1001-C2 — TÉLÉMÉTRIE MINIMALE des exceptions frontend.
//
// Pourquoi : une page blanche signalée par Mario (N°998-F) n'a laissé AUCUNE trace —
// le front ne remontait aucune erreur JS. Ce module donne une trace EXPLOITABLE de la
// prochaine exception réelle, ⛔ sans secret ni donnée client.
//
// 🔑 Ce qui est envoyé : type d'erreur, message NETTOYÉ et tronqué, noms de composants
//    (pile React), écran métier courant, version du bundle, navigateur.
// ⛔ Ce qui n'est JAMAIS envoyé : URL complète / query, localStorage, cookie, jeton,
//    corps de requête, données de dossier/client. Le message lui-même est nettoyé :
//    e-mails, suites de chiffres (SIRET, téléphone, IBAN…), jetons longs ⇒ masqués.
// Transport : PostHog DÉJÀ chargé par public/index.html (aucun nouveau tiers). Absent ⇒
//    on écrit dans la console et on n'échoue JAMAIS (la télémétrie ne doit pas casser).

const MAX = 300;

export function nettoyer(texte) {
  return String(texte ?? "")
    .replace(/[\w.+-]+@[\w-]+(\.[\w-]+)+/g, "<email>")
    .replace(/\b(?:Bearer\s+)?[A-Za-z0-9_-]{24,}\b/g, "<jeton>")
    .replace(/\d[\d\s.-]{5,}\d/g, "<nombre>")
    .slice(0, MAX);
}

const masquerPartie = (t) => String(t).replace(/\s+/g, " ").replace(/[\w.+-]+@[\w-]+(\.[\w-]+)+/g, "<email>").replace(/\b(?:Bearer\s+)?[A-Za-z0-9_-]{40,}\b/g, "<jeton>");

/** Pile d'appels RÉDUITE à `fonction@fichier:ligne:col` (⛔ ni chemin ni URL ni query) : positions DANS le code (bundle minifié), aucune donnée. Masquée, ≤ 12 cadres, ≤ 1200 car. */
export function nettoyerPile(pile) {
  const cadres = [];
  for (const ligne of String(pile || "").split("\n")) {
    const m = ligne.match(/at\s+(?:(.*?)\s+\()?(?:[^\s()]*[\\/])?([^\s()\\/?#]+?)(?:\?[^\s():]*)?:(\d+):(\d+)\)?\s*$/);
    if (m) cadres.push(`${masquerPartie(m[1] || "<anonyme>")}@${masquerPartie(m[2])}:${m[3]}:${m[4]}`);   // ⚠️ masquer AVANT d'assembler : « fn@fichier » ressemble à un e-mail
    if (cadres.length >= 12) break;
  }
  return cadres.join(" | ").slice(0, 1200);
}

function pileComposants(info) {
  // `componentStack` = « at Composant (url:ligne:col) » ⇒ on ne garde que les NOMS.
  const brut = String(info?.componentStack || "");
  const noms = [...brut.matchAll(/at (\w+)/g)].map((m) => m[1]);
  return noms.slice(0, 12).join(" < ");
}

function versionBundle() {
  try {
    const s = [...document.scripts].map((x) => x.src).find((u) => /\/static\/js\/main\.[0-9a-f]+\.js/.test(u));
    return s ? s.split("/").pop() : null;
  } catch { return null; }
}

export function construireEvenement(erreur, { ecran = null, info = null, source = "boundary" } = {}) {
  return {
    source,                                   // boundary | window.error | unhandledrejection
    type: nettoyer(erreur?.name || typeof erreur),
    message: nettoyer(erreur?.message || erreur),
    composants: pileComposants(info),
    pile: nettoyerPile(erreur?.stack),
    ecran: ecran ? String(ecran).slice(0, 60) : null,
    chemin: (() => { try { return window.location.pathname; } catch { return null; } })(),
    bundle: versionBundle(),
    navigateur: (() => { try { return navigator.userAgent.slice(0, 160); } catch { return null; } })(),
  };
}

// ── ENVOI AU SERVEUR (P0 Chrome) : l'erreur RÉELLE est enregistrée côté serveur (route authentifiée, scopée tenant, bornée) EN PLUS de PostHog (qui peut être bloqué chez l'utilisateur).
//    ⛔ Ne lève jamais, ⛔ ne redéclenche jamais le garde-fou, ⛔ au plus 5 envois par chargement de page, ⛔ jamais sans session.
const _envoyes = new Set();
export function envoyerAuServeur(evt) {
  try {
    if (typeof fetch !== "function" || _envoyes.size >= 5) return;
    const jeton = window.localStorage.getItem("session_token");
    if (!jeton) return;
    const cle = `${evt.type}|${evt.message}|${evt.composants}`;
    if (_envoyes.has(cle)) return;
    _envoyes.add(cle);
    fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/metier/telemetrie/erreur-front`, {
      method: "POST", keepalive: true, credentials: "include",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${jeton}` },
      body: JSON.stringify(evt),
    }).catch(() => {});
  } catch { /* ⛔ la télémétrie ne doit JAMAIS lever */ }
}
export const _reinitialiserEnvoyes = () => _envoyes.clear();   // tests

/** 🔴 P0 Chrome — une valeur serveur NON-TEXTE reçue là où l'écran attend un texte (cause connue du crash React #31 « objet rendu comme enfant ») a été NEUTRALISÉE.
 *  Signalé UNE fois par page et par champ : le NOM du champ et le TYPE reçu (object/array/number/boolean/function) — ⛔ JAMAIS la valeur. */
export const typeRecu = (v) => (v === null ? "null" : Array.isArray(v) ? "array" : typeof v);
export function signalerValeurNonTexte(champ, valeur, { ecran = "devis-resume" } = {}) {
  try {
    const evt = { source: "garde-fou", type: "valeur non-texte au Résumé", message: `${String(champ).slice(0, 60)} : ${typeRecu(valeur)}`,
                  composants: "EtapeValidation", ecran, chemin: (() => { try { return window.location.pathname; } catch { return null; } })(), bundle: versionBundle(),
                  navigateur: (() => { try { return navigator.userAgent.slice(0, 160); } catch { return null; } })() };
    envoyerAuServeur(evt);
    // eslint-disable-next-line no-console
    console.warn("[OH garde-fou]", evt.message);
  } catch { /* ⛔ la télémétrie ne doit JAMAIS lever */ }
}

export function signalerErreur(erreur, contexte = {}) {
  let evt = null;
  try {
    evt = construireEvenement(erreur, contexte);
    if (window.posthog && typeof window.posthog.capture === "function") {
      window.posthog.capture("oh_erreur_frontend", evt);
    }
    envoyerAuServeur(evt);
    // eslint-disable-next-line no-console
    console.error("[OH erreur frontend]", evt);
  } catch { /* ⛔ la télémétrie ne doit JAMAIS lever */ }
  return evt;
}

let _installe = false;
export function installerCaptureGlobale() {
  if (_installe || typeof window === "undefined") return;
  _installe = true;
  window.addEventListener("error", (e) => signalerErreur(e?.error || e?.message, { source: "window.error" }));
  window.addEventListener("unhandledrejection", (e) => signalerErreur(e?.reason, { source: "unhandledrejection" }));
}
